/**
 * Financial Helpers - Tolerância de centavos
 * 
 * Funções centralizadas para tratar o "erro do centavo" em todo o sistema financeiro.
 * Quando a diferença entre valor total e valor pago é menor que a tolerância,
 * o sistema considera como quitado (puramente visual, sem alterar dados no banco).
 */

/** Tolerância máxima em reais para considerar uma conta como quitada */
export const PENNY_TOLERANCE = 0.02;

/** F-FIN P2: epsilon da régua única de saldo (coluna `balance`), igual ao das RPCs de baixa */
export const BALANCE_EPSILON = 0.009;

/**
 * Verifica se uma conta está efetivamente quitada, considerando tolerância de centavos.
 * F-FIN P2: quando `balance` (coluna gerada total − pago − desconto) é informado, ele é a régua
 * (epsilon 0.009); senão cai no legado total − pago (±2 centavos).
 *
 * @param totalValue - Valor total da conta
 * @param paidValue - Valor já pago
 * @param balance - Saldo do banco (total − pago − desconto), quando disponível
 * @returns true se o saldo está dentro da tolerância
 */
export function isFullyPaid(totalValue: number, paidValue: number, balance?: number | null): boolean {
  if (typeof balance === 'number') {
    return balance <= BALANCE_EPSILON;
  }
  const remaining = totalValue - paidValue;
  return Math.abs(remaining) <= PENNY_TOLERANCE;
}

/**
 * Retorna o valor restante para exibição, aplicando tolerância de centavos.
 * Valores dentro da tolerância são exibidos como 0.
 * Valores negativos (pagamento excedente) também são tratados como 0.
 * 
 * @param totalValue - Valor total da conta
 * @param paidValue - Valor já pago
 * @returns Valor restante ajustado (0 se dentro da tolerância ou negativo)
 */
export function displayRemainingValue(totalValue: number, paidValue: number, balance?: number | null): number {
  // F-FIN P2: balance do banco (total − pago − desconto) é a régua quando presente
  const remaining = typeof balance === 'number' ? balance : totalValue - paidValue;
  if (Math.abs(remaining) <= PENNY_TOLERANCE) {
    return 0;
  }
  // Nunca exibir saldo negativo (pagamento excedente)
  if (remaining < 0) {
    return 0;
  }
  return remaining;
}

export interface ReceivablePaymentGuardInput {
  status: string;
  total_value: number;
  paid_value?: number | null;
  /** F-FIN P2: coluna gerada do banco; quando presente é a régua */
  balance?: number | null;
  discount_value?: number | null;
}

/**
 * Parcela ainda aceita baixa manual ("Informar Recebimento").
 * Oculta quando quitada ou parcial com saldo zerado.
 * F-FIN P2: saldo = balance ?? total − pago − desconto, epsilon 0.009.
 */
export function canInformReceivablePayment(receivable: ReceivablePaymentGuardInput): boolean {
  if (
    receivable.status === 'paid' ||
    receivable.status === 'cancelled' ||
    receivable.status === 'renegotiated'
  ) {
    return false;
  }
  const balance =
    receivable.balance ??
    receivable.total_value - (receivable.paid_value ?? 0) - (receivable.discount_value ?? 0);
  if (receivable.status === 'partial' && balance <= BALANCE_EPSILON) {
    return false;
  }
  return true;
}
